import { useEffect, useRef, useState, useCallback } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/* ──────────── 类型 ──────────── */
interface ChatMsg {
  role: 'user' | 'ai';
  content: string;
}

interface SceneReply {
  step1: string;
  step2: string;
  step3: string;
}

type ViewMode = 'scenes' | 'popup' | 'chat' | 'limit';

/* ──────────── 4个预设场景 ──────────── */
const SCENES: { title: string; desc: string }[] = [
  { title: '如何向团队宣布重组而不引发恐慌？', desc: '组织重组期' },
  { title: 'AI重构后，老员工抵触怎么办？', desc: '技术变革期' },
  { title: '销售冲刺期，如何快速提升团队士气？', desc: '高压目标期' },
  { title: '核心高管离职，如何稳住军心？', desc: '核心变动期' },
];

/* ──────────── AI方案库（三段式）──────────── */
const sceneReplies: Record<string, SceneReply> = {
  '如何向团队宣布重组而不引发恐慌？': {
    step1: '宣布重组时，团队会经历典型的"不确定性焦虑"：员工会过度解读信息、担心岗位安全、信息传递中出现失真和谣言。心理学上，这叫"威胁警觉"——人在面对不确定时，大脑杏仁核会自动放大负面预期。',
    step2: '① 宣布时先用"稳定锚定法"开场："今天要和你们聊的是公司未来半年最重要的战略调整。在讲变化之前，我想先明确三个不变：我们的核心业务方向不变、团队对每个人的承诺不变、对你们职业成长的投入不变。"\n\n② 紧接着用"参与式提问"收束恐慌："我知道大家会有很多问题，接下来48小时内，每人可以提交你最关心的3个问题，我们会逐一公开回应。"——这句话把单向宣告变成了双向对话，心理学上叫"控制感恢复"。',
    step3: '针对您企业的具体情况，需预约顾问，让10分钟的沟通助力您的职场工作成效。',
  },
  'AI重构后，老员工抵触怎么办？': {
    step1: '老员工对AI的抵触，表面是"技术恐惧"，深层是"身份威胁"——他们担心多年积累的经验被算法替代，自我价值感受到冲击。心理学上这叫"胜任感动摇"，如果不及时干预，会快速蔓延为习得性无助。',
    step2: '① 用"价值重框法"转换叙事："公司引入AI，不是要替代你们的经验，而是要把你们从重复性工作中解放出来，让你们去做更有判断力、更需要人情味的事。你们10年的行业洞察，AI学不会。"\n\n② 落地一个"AI搭档实验"：让抵触最强的老员工在1周内只使用AI处理他最熟悉的一个任务，然后让他自己说出"哪里比我想的好、哪里还需要我"——自我说服比任何培训都有效。',
    step3: '针对您企业的具体情况，需预约顾问，让10分钟的沟通助力您的职场工作成效。',
  },
  '销售冲刺期，如何快速提升团队士气？': {
    step1: '冲刺期士气低落，通常不是"激励不够"，而是"心理资源枯竭"。持续高压下，团队的"心理账户"被透支：每个人都知道要冲，但身体和心理已经发出了倦怠信号。这时候再喊口号，只会引发情绪性抵触。',
    step2: '① 用"微胜利法"重建掌控感：把大目标拆解到每天只能完成1-2件的最小行动，晨会第一件事就是让每个人说出"昨天我搞定了什么"——不是承诺今天做什么，而是确认昨天已完成什么。心理学上这叫"自我效能感积累"。\n\n② 每天下午4点安排15分钟"情绪透气窗"：所有人放下电话，主管只说一句"今天最难的一单是什么，卡在哪了"——不点评、不给建议，只倾听。这15分钟能显著降低团队的皮质醇水平。',
    step3: '针对您企业的具体情况，需预约顾问，让10分钟的沟通助力您的职场工作成效。',
  },
  '核心高管离职，如何稳住军心？': {
    step1: '核心高管离职后的72小时，是团队"心理地震"的窗口期。员工会经历三个阶段：震惊→猜测→站队。如果不主动干预，信息真空会被谣言填满，剩余高管的每一个动作都会被过度解读，形成"二次恐慌"。',
    step2: '① 24小时内召开"透明沟通会"，开场话术："XX的离开对我们是一个损失。我知道大家最关心三个问题：业务会不会受影响、你们的发展会不会受影响、接下来谁负责。今天我会一个一个说清楚。"——心理学上这叫"预期管理"，主动说出员工想问但不敢问的。\n\n② 紧接着给出一个"确定性锚点"：公布接下来30天内的3件确定事项（如：客户交接方案、临时负责人选、团队会议节奏），让员工感到"虽然人走了，但系统在运转"。',
    step3: '针对您企业的具体情况，需预约顾问，让10分钟的沟通助力您的职场工作成效。',
  },
};

/* ──────────── 心力教练递进式追问库（每轮不同，层层深入）──────────── */
const sceneFollowUps: Record<string, string[]> = {
  '如何向团队宣布重组而不引发恐慌？': [
    '当你想到要开口宣布重组时，你身体最先有反应的部位是哪里？那个紧绷感在告诉你什么？',
    '如果你把团队的恐慌程度从1到10打分，现在大概在几分？哪些人可能比你预估的更冷静？',
    '在过去的管理中，有没有一次你成功稳住了团队的情绪？那次你做对了什么，这次可以复用？',
    '如果有一个最反对重组的员工，坐下来和你坦诚对话，他真正担心的可能不是他说的那些理由——你觉得那背后藏的是什么？',
    '想象一下，三个月后团队回头看这次重组，你觉得他们会说"幸好当时…"，还是"可惜当时没…"？',
    '如果现在让你用一句话向团队传递"安全感"，你会怎么说？那句话和你最初想说的是否一样？',
    '重组消息宣布后的72小时，你最害怕发生的场景是什么？那个场景里，是谁的什么反应让你最不安？',
    '如果把这个重组看作一个故事的开篇，你希望自己是"带领者"、"陪伴者"还是"缓冲者"？这个身份选择会影响你怎么开口。',
    '团队中有没有一个你信任的人，可以在宣布前先做一次"小规模彩排"？选谁，为什么是他？',
    '最后一个问题——如果这次重组最终成功了，团队会记住你的哪一个动作？你现在就可以开始做那个动作。',
  ],
  'AI重构后，老员工抵触怎么办？': [
    '当你说"抵触"的时候，你观察到的是什么样的具体行为？是沉默、抱怨，还是消极配合？每种行为背后的恐惧不一样。',
    '如果让你站在那个最抵触的老员工的位置，用他的眼睛看这件事——他觉得自己正在失去什么？那个"失去"是真实的，还是他想象的？',
    '在这些老员工中，有没有一个人，虽然嘴上反对，但行动上还在配合？他内在的矛盾可能是什么？',
    '如果让你给这些老员工讲一个"AI来了之后，你变得更值钱了"的故事，你会怎么讲？那个故事的核心情节是什么？',
    '你曾经有没有一项新技能，一开始排斥、后来慢慢接受了？那段转变里，是什么让你放下了抗拒？',
    '如果有一个老员工主动来找你谈AI的事，你希望他问你什么？那个问题本身就是突破口。',
    '抵触情绪在团队里是怎么传播的？是某个人带动了气氛，还是大家本来就都有这个担忧？找到"第一块倒下的多米诺"很重要。',
    '如果你只能做一个最小、最不费力的动作来松动这种抵触，你会选择什么？是换一个人对话、调整一个流程，还是展示一个案例？',
    '三个月后，如果这些老员工已经接受了AI，他们回顾这段抵触时，会怎么描述自己的心情？那个描述对你现在的做法有什么启发？',
    '最后一个问题——老员工身上有哪些经验和洞察，是AI永远替代不了的？如果让他们意识到这一点，抵触会变成什么？',
  ],
  '销售冲刺期，如何快速提升团队士气？': [
    '当你说"士气低落"的时候，你观察到的是什么？是开会没人说话、月底业绩差，还是有人开始提离职？每个信号背后的原因不同。',
    '如果让你选一个最近士气最低落的团队成员，和他坐下来聊，你觉得他最想说但没说的话是什么？',
    '这个冲刺目标，是团队自己认的，还是上面压下来的？如果是压下来的，团队的"心理契约"已经被打破了，士气不是靠激励能拉回来的。',
    '在你过去的管理中，有没有一次团队从低谷走出来的经历？那次转折点是哪个微小的动作触发的？',
    '如果明天早上你只能做一个改变，让团队感受到"我被看见了"，你会做什么？那个动作要花多长时间？',
    '团队里有没有一个"隐形领导者"——不是职级最高的，但大家会听他/她的？这个人现在的状态如何？他的态度会传染。',
    '你把"士气"和"动力"混为一谈了吗？士气是情绪，动力是目标。情绪需要被接纳，目标需要被拆解。你现在的团队在缺哪一个？',
    '如果让你给团队讲一个"为什么这个冲刺值得打"的故事，你会怎么讲？那个故事里，谁是英雄、谁是受益者？',
    '想象一下，这个冲刺期结束后，团队成员最可能会记住的不是业绩数字，而是一个瞬间。你希望那个瞬间是什么？你现在就可以创造它。',
    '最后一个问题——如果团队现在只能做成一件事，大家会不约而同地选什么？那个共识本身就是士气的种子。',
  ],
  '核心高管离职，如何稳住军心？': [
    '高管离开的消息传开后，你注意到团队的第一个反应是什么？是震惊、猜测，还是有人开始更新简历？第一个反应决定了干预的紧迫性。',
    '如果让你选一个团队里"情绪最不稳定"的人，他/她的不稳定是因为担心自己的位置、惋惜离开的人，还是怀疑公司的未来？每种担心需要不同的回应。',
    '离职高管和团队之间，有没有一段"未完成的故事"？比如承诺过但没兑现的事、没来得及交接的项目——这些未完成感会放大焦虑。',
    '如果你是团队中的一员，不是管理者，你现在最希望从领导嘴里听到的一句话是什么？那句话和你准备说的，一样吗？',
    '在你的职业生涯中，有没有经历过"重要的人离开，团队反而变好了"的情况？那次做对了什么，这次可以复制？',
    '团队中有没有一个人，你可以先私下沟通，让他/她成为你的"情绪锚点"？选谁，为什么是他？',
    '如果72小时后你还没有主动开口，团队会用什么故事填补信息真空？那个故事可能比你想象的更可怕。',
    '稳定军心的关键，是"给出确定性"还是"承认不确定性"？对现在的团队，哪一个更有力量？',
    '如果三个月后回头看这次危机，你希望团队记住的是你做的哪一件事？那件 smallest 但最有力量的事。',
    '最后一个问题——离职高管带走的不只是能力，还有一种"团队感"。你打算用什么来替代那个"团队感"？',
  ],
};

/* ──────────── 轮次限制工具 ──────────── */
const STORAGE_KEY = 'oc_heart_coach_v2';
const MAX_SESSIONS = 2;
const MAX_TURNS = 10;

function getStorage() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch { return {}; }
}
function setStorage(data: unknown) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}
function getToday() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function getUsage() {
  const s = getStorage();
  const today = getToday();
  if (s.date !== today) return { sessionsUsed: 0, currentTurns: 0 };
  return { sessionsUsed: s.sessionsUsed || 0, currentTurns: s.currentTurns || 0 };
}
function startNewSession() {
  const s = getStorage();
  const today = getToday();
  if (s.date !== today) {
    setStorage({ date: today, sessionsUsed: 1, currentTurns: 0 });
  } else {
    setStorage({ ...s, sessionsUsed: (s.sessionsUsed || 0) + 1, currentTurns: 0 });
  }
}
function incrementTurn() {
  const s = getStorage();
  setStorage({ ...s, currentTurns: (s.currentTurns || 0) + 1 });
}

/* ──────────── 第10轮总结生成 ──────────── */
function generateSummary(msgs: ChatMsg[], scene: string): string {
  // 提取用户的所有发言
  const userMsgs = msgs.filter(m => m.role === 'user').map(m => m.content);
  const userInputs = userMsgs.length > 0 ? userMsgs.join('；') : '你的分享';

  return `我们的对话即将结束。让我为你做一个简短的梳理——\n\n在这场关于「${scene}」的探讨中，你谈到了：${userInputs}。\n\n我注意到几个关键的觉察点：\n\n① 每个组织变革的困境，表面是流程和结构的问题，深层都是人心的议题。你已经看到了那些"看不见的情绪线索"。\n\n② 改变不是从"知道该怎么做"开始的，而是从"愿意面对真实处境"开始的——你已经在路上了。\n\n③ 真正有效的干预，从来不是套用标准话术，而是基于对你们团队独特动力的理解，量身定制方案。\n\n——\n\n我是你的心力教练，一名资深职场心理教练，也是个人和团体心理咨询师。\n\n刚才的对话只是一个开始。如果要真正把这些洞察转化为团队的变化，还需要更系统的诊断和持续的陪伴。\n\n针对您企业的具体情况，完整方案需预约顾问，让10分钟的沟通助力您的职场工作成效。`;
}

/* ──────────── 企微客服 ──────────── */
const WEWORK_KF_LINK = 'https://work.weixin.qq.com/kfid/kfcc1729b8642e445e3';

/* ──────────── 主组件 ──────────── */
export default function AiChatSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const [view, setView] = useState<ViewMode>('scenes');
  const [selectedScene, setSelectedScene] = useState<string>('');
  const [chatMsgs, setChatMsgs] = useState<ChatMsg[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [usage, setUsage] = useState(getUsage());
  const [userInput, setUserInput] = useState('');
  const [popupAnim, setPopupAnim] = useState(false);

  /* 滚动动画 */
  useEffect(() => {
    if (!sectionRef.current) return;
    const ctx = gsap.context(() => {
      gsap.from(sectionRef.current!.querySelectorAll('.reveal-el'), {
        y: 30, opacity: 0, duration: 0.8, stagger: 0.12, ease: 'power2.out',
        scrollTrigger: { trigger: sectionRef.current, start: 'top 75%', once: true },
      });
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  /* 容器内部滚动，不触发页面级滚动 */
  useEffect(() => {
    const container = chatContainerRef.current;
    const endEl = chatEndRef.current;
    if (container && endEl) {
      const scrollTop = endEl.offsetTop - container.offsetTop + endEl.offsetHeight - container.clientHeight + 20;
      container.scrollTo({ top: Math.max(0, scrollTop), behavior: 'smooth' });
    }
  }, [chatMsgs, isTyping]);

  /* 检查限制 */
  const checkLimit = useCallback((): boolean => {
    const u = getUsage();
    setUsage(u);
    if (u.sessionsUsed >= MAX_SESSIONS) { setView('limit'); return false; }
    return true;
  }, []);

  const checkTurns = useCallback((): boolean => {
    const u = getUsage();
    setUsage(u);
    if (u.currentTurns >= MAX_TURNS) { setView('limit'); return false; }
    return true;
  }, []);

  /* 点击场景 → 弹窗方案 */
  const handleSceneClick = (title: string) => {
    if (!checkLimit()) return;
    setSelectedScene(title);
    setView('popup');
    setTimeout(() => setPopupAnim(true), 50);
  };

  /* 返回场景页 */
  const handleBackToScenes = () => {
    setPopupAnim(false);
    setTimeout(() => { setView('scenes'); setSelectedScene(''); setUsage(getUsage()); }, 300);
  };

  /* 进入模拟对话 */
  const handleStartChat = () => {
    startNewSession();
    setUsage(getUsage());
    setPopupAnim(false);
    setTimeout(() => {
      setView('chat');
      // 初始AI引导
      setChatMsgs([{
        role: 'ai',
        content: '你好，我是你的心力教练。我是一名资深职场心理教练，也是个人和团体心理咨询师。\n\n刚才你已经看到了初步方案——现在，我想邀请你往深里再走一步。你可以说说你当下的真实处境，或者回应我接下来的提问。\n\n记住：这里没有标准答案，只有对你团队最有价值的觉察。',
      }]);
    }, 300);
  };

  /* 发送消息（模拟对话） */
  const handleSend = () => {
    if (!userInput.trim()) return;
    if (!checkTurns()) return;

    const text = userInput.trim();
    setUserInput('');
    incrementTurn();
    setUsage(getUsage());

    setChatMsgs(prev => [...prev, { role: 'user', content: text }]);
    setIsTyping(true);

    // 选择追问话术——按场景、按轮次递进，不重复
    const turnIdx = getUsage().currentTurns;
    const followUps = sceneFollowUps[selectedScene] || sceneFollowUps[SCENES[0].title];
    const followUp = followUps[Math.min(turnIdx, followUps.length - 1)];

    setTimeout(() => {
      setIsTyping(false);
      if (turnIdx >= MAX_TURNS - 1) {
        // 第10轮：总结 + 10秒后跳转
        const summaryMsg = generateSummary(chatMsgs, selectedScene);
        setChatMsgs(prev => [...prev, { role: 'ai', content: summaryMsg }]);
        setTimeout(() => setView('limit'), 10000);
      } else {
        setChatMsgs(prev => [...prev, { role: 'ai', content: followUp }]);
      }
    }, 1000);
  };

  /* 快捷追问 */
  const handleQuickAsk = (q: string) => {
    setUserInput(q);
  };

  const reply = selectedScene ? sceneReplies[selectedScene] : null;
  const progressPct = Math.min(100, (usage.currentTurns / MAX_TURNS) * 100);

  return (
    <section id="ai-chat" ref={sectionRef} className="relative py-24 lg:py-32" style={{ background: '#fcfcfc' }}>
      <div className="max-w-5xl mx-auto px-6 lg:px-12">

        {/* ── 标题区 ── */}
        <div id="org-change" className="text-center mb-10 reveal-el">
          <p className="mb-3" style={{ fontSize: '0.72rem', color: '#a08e78', letterSpacing: '0.12em' }}>
            孔隙心理 — 组织变革心力教练
          </p>
          <h2 className="font-body" style={{
            fontSize: 'clamp(1.6rem, 4vw, 2.4rem)', fontWeight: 700, lineHeight: 1.2,
            color: '#1A2A4A', wordBreak: 'keep-all',
            textShadow: '1px 1px 0 #d4c4b0, 2px 2px 0 rgba(180,160,130,0.2)',
          }}>
            组织变革心力教练
          </h2>
          <p className="mt-3 mx-auto max-w-md" style={{ fontSize: '0.82rem', lineHeight: 1.7, color: '#6e5e50' }}>
            不闲聊，只解决真实的组织变革难题。选一个你正在头疼的场景，让专业AI教练引导，初步建立应对之策的思维。
          </p>
          {/* 次数提示 */}
          <div className="mt-4">
            <span style={{
              fontSize: '0.7rem',
              color: usage.sessionsUsed >= MAX_SESSIONS ? '#D94A4A' : '#a08e78',
              background: usage.sessionsUsed >= MAX_SESSIONS ? '#FDF0F0' : '#f5f0e8',
              padding: '4px 14px', borderRadius: '999px', display: 'inline-block',
            }}>
              今日剩余体验：{Math.max(0, MAX_SESSIONS - usage.sessionsUsed)} / {MAX_SESSIONS} 次
            </span>
          </div>
        </div>

        {/* ════════════════════════════════════════════
            VIEW: 场景选择页
        ════════════════════════════════════════════ */}
        {view === 'scenes' && (
          <div id="org-behavior" className="reveal-el mx-auto" style={{ maxWidth: 720 }}>
            <p style={{ fontSize: '0.85rem', color: '#2c2420', fontWeight: 600, marginBottom: '1.25rem', textAlign: 'center' }}>
              选一个你正在头疼的场景
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {SCENES.map(s => (
                <button
                  key={s.title}
                  className="text-left py-5 px-5 transition-all duration-250"
                  style={{
                    background: '#fff', border: '1.5px solid #e8e0d4', borderRadius: '14px',
                    fontSize: '0.88rem', color: '#2c2420', fontWeight: 600,
                    cursor: 'pointer', lineHeight: 1.5,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.borderColor = '#E8751A';
                    e.currentTarget.style.background = '#fff8f3';
                    e.currentTarget.style.boxShadow = '0 4px 20px rgba(232,117,26,0.12)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.borderColor = '#e8e0d4';
                    e.currentTarget.style.background = '#fff';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.03)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                  onClick={() => handleSceneClick(s.title)}
                >
                  <span style={{ fontSize: '1.3rem', display: 'block', marginBottom: '6px' }}>💬</span>
                  {s.title}
                  <span style={{ fontSize: '0.7rem', color: '#a08e78', display: 'block', marginTop: '4px', fontWeight: 400 }}>
                    {s.desc}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════
            VIEW: 方案弹窗
        ════════════════════════════════════════════ */}
        {view === 'popup' && reply && (
          <div className="fixed inset-0 z-50 flex items-start justify-center px-4 py-8" style={{
            background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)',
            opacity: popupAnim ? 1 : 0, transition: 'opacity 0.3s ease',
          }}>
            <div className="w-full max-w-xl max-h-[90vh] overflow-y-auto" style={{
              background: '#fff', borderRadius: '16px',
              boxShadow: '0 24px 80px rgba(0,0,0,0.2)',
              transform: popupAnim ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.97)',
              transition: 'transform 0.35s cubic-bezier(0.16,1,0.3,1)',
            }}>
              {/* 弹窗头部 */}
              <div style={{ padding: '1.5rem 1.5rem 0' }}>
                <div className="flex items-center gap-3 mb-1">
                  <div style={{
                    width: 36, height: 36, borderRadius: '50%',
                    background: 'linear-gradient(135deg, #1A2A4A, #2d4a6e)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.8rem', color: '#fff', fontWeight: 600,
                  }}>顾</div>
                  <div>
                    <p style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1A2A4A' }}>变革顾问方案</p>
                    <p style={{ fontSize: '0.7rem', color: '#a08e78' }}>AI心力教练 · 后现代心理学</p>
                  </div>
                </div>
                <p style={{ fontSize: '0.82rem', color: '#E8751A', fontWeight: 600, marginTop: '0.75rem', paddingLeft: '3rem' }}>
                  {selectedScene}
                </p>
              </div>

              {/* 三段式方案 */}
              <div style={{ padding: '1.25rem 1.5rem' }}>
                {/* 第一步 */}
                <div className="mb-4" style={{ background: '#f8f9fb', borderRadius: '12px', padding: '1rem 1.25rem', border: '1px solid #e8eaed' }}>
                  <div style={{
                    display: 'inline-block', fontSize: '0.65rem', fontWeight: 700, color: '#fff',
                    background: '#1A2A4A', padding: '3px 10px', borderRadius: '4px', marginBottom: '0.5rem', letterSpacing: '0.03em',
                  }}>第一步 · 典型心理反应</div>
                  <p style={{ fontSize: '0.82rem', lineHeight: 1.75, color: '#2c2420', whiteSpace: 'pre-line' }}>{reply.step1}</p>
                </div>

                {/* 第二步 */}
                <div className="mb-4" style={{ background: '#f0f7f0', borderRadius: '12px', padding: '1rem 1.25rem', border: '1px solid #d8e8d8' }}>
                  <div style={{
                    display: 'inline-block', fontSize: '0.65rem', fontWeight: 700, color: '#fff',
                    background: '#3A9B6E', padding: '3px 10px', borderRadius: '4px', marginBottom: '0.5rem', letterSpacing: '0.03em',
                  }}>第二步 · 立即可执行</div>
                  <p style={{ fontSize: '0.82rem', lineHeight: 1.75, color: '#2c2420', whiteSpace: 'pre-line' }}>{reply.step2}</p>
                </div>

                {/* 第三步 */}
                <div style={{
                  background: '#fff8f3', borderRadius: '12px', padding: '1rem 1.25rem',
                  border: '1.5px solid #E8751A',
                }}>
                  <div style={{
                    display: 'inline-block', fontSize: '0.65rem', fontWeight: 700, color: '#fff',
                    background: '#E8751A', padding: '3px 10px', borderRadius: '4px', marginBottom: '0.5rem', letterSpacing: '0.03em',
                  }}>第三步 · 深度方案</div>
                  <p style={{ fontSize: '0.82rem', lineHeight: 1.75, color: '#E8751A', fontWeight: 600, whiteSpace: 'pre-line' }}>{reply.step3}</p>
                </div>
              </div>

              {/* 底部按钮 */}
              <div style={{ padding: '0 1.5rem 1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem' }}>
                <button
                  className="w-full max-w-xs py-3.5 transition-all duration-200 hover:scale-[1.02]"
                  style={{
                    background: 'linear-gradient(135deg, #E8751A, #d46816)', color: '#fff',
                    borderRadius: '9999px', fontSize: '0.9rem', fontWeight: 700,
                    border: 'none', cursor: 'pointer',
                    boxShadow: '0 4px 16px rgba(232,117,26,0.35), inset 0 1px 0 rgba(255,255,255,0.3)',
                  }}
                  onClick={handleStartChat}
                >
                  🎯 AI教练引导
                </button>
                <button
                  className="py-2 transition-all duration-200"
                  style={{
                    background: 'transparent', color: '#D94A4A',
                    border: 'none', fontSize: '0.85rem', fontWeight: 500,
                    cursor: 'pointer', letterSpacing: '0.02em',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.color = '#ff6b6b'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = '#D94A4A'; }}
                  onClick={handleBackToScenes}
                >
                  <span style={{ color: '#ff8080', marginRight: '4px' }}>←</span>返回场景选择
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════
            VIEW: 模拟对话
        ════════════════════════════════════════════ */}
        {view === 'chat' && (
          <div className="reveal-el mx-auto" style={{ maxWidth: 720 }}>
            {/* 对话头部 */}
            <div className="flex items-center justify-between mb-4" style={{ padding: '0 0.5rem' }}>
              <div className="flex items-center gap-2">
                <div style={{
                  width: 32, height: 32, borderRadius: '50%',
                  background: 'linear-gradient(135deg, #1A2A4A, #2d4a6e)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.75rem', color: '#fff', fontWeight: 600,
                }}>教</div>
                <div>
                  <p style={{ fontSize: '0.82rem', fontWeight: 600, color: '#1A2A4A' }}>AI心力教练</p>
                  <p style={{ fontSize: '0.65rem', color: '#a08e78' }}>后现代心理学 · 循环提问 · 叙事疗法</p>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <p style={{ fontSize: '0.65rem', color: '#a08e78' }}>
                  本轮 {usage.currentTurns} / {MAX_TURNS}
                </p>
                <div style={{ width: 80, height: 4, background: '#e8e0d4', borderRadius: 999, marginTop: 4, overflow: 'hidden' }}>
                  <div style={{ width: `${progressPct}%`, height: '100%', background: usage.currentTurns >= MAX_TURNS ? '#D94A4A' : '#E8751A', borderRadius: 999, transition: 'width 0.3s' }} />
                </div>
              </div>
            </div>

            {/* 对话区域 */}
            <div
              ref={chatContainerRef}
              style={{
                background: '#faf9f7', border: '1px solid #e8e0d4', borderRadius: '14px',
                minHeight: 300, maxHeight: 500, overflowY: 'auto', padding: '1.25rem 1.25rem',
              }}
            >
              {chatMsgs.map((msg, idx) => (
                <div key={idx} className="mb-4">
                  {msg.role === 'user' ? (
                    <div className="flex justify-end">
                      <div style={{
                        background: '#E8751A', color: '#fff', padding: '0.7rem 1rem',
                        borderRadius: '12px 12px 2px 12px', fontSize: '0.82rem',
                        maxWidth: '85%', lineHeight: 1.6, whiteSpace: 'pre-line',
                      }}>{msg.content}</div>
                    </div>
                  ) : (
                    <div className="flex justify-start">
                      <div style={{ maxWidth: '90%' }}>
                        <div className="flex items-center gap-2 mb-1.5">
                          <div style={{
                            width: 26, height: 26, borderRadius: '50%',
                            background: 'linear-gradient(135deg, #1A2A4A, #2d4a6e)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '0.65rem', color: '#fff', fontWeight: 600,
                          }}>教</div>
                          <span style={{ fontSize: '0.68rem', color: '#a08e78' }}>心力教练</span>
                        </div>
                        <div style={{
                          background: '#fff', padding: '0.85rem 1rem', borderRadius: '10px',
                          fontSize: '0.82rem', lineHeight: 1.75, color: '#2c2420',
                          border: '1px solid #e8e0d4', whiteSpace: 'pre-line',
                        }}>
                          {msg.content}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {isTyping && (
                <div className="flex items-center gap-2" style={{ padding: '0.5rem 0' }}>
                  <div style={{
                    width: 26, height: 26, borderRadius: '50%',
                    background: 'linear-gradient(135deg, #1A2A4A, #2d4a6e)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.65rem', color: '#fff',
                  }}>教</div>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#c8bfb0', animation: 'typingBounce 1.2s infinite 0s' }} />
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#c8bfb0', animation: 'typingBounce 1.2s infinite 0.2s' }} />
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#c8bfb0', animation: 'typingBounce 1.2s infinite 0.4s' }} />
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            {/* 快捷追问 + 输入 */}
            <div className="mt-3" style={{ padding: '0 0.25rem' }}>
              {/* 快捷按钮 */}
              <div className="flex flex-wrap gap-2 mb-3">
                {['我该怎么开始？', '团队有人特别消极', '这些方法试过但没用', '下一步该做什么？'].map(q => (
                  <button
                    key={q}
                    className="py-1.5 px-3 transition-all duration-200"
                    style={{
                      background: '#f5f0e8', border: '1px solid #e8e0d4', borderRadius: '8px',
                      fontSize: '0.72rem', color: '#6e5e50', cursor: 'pointer',
                    }}
                    onClick={() => handleQuickAsk(q)}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#E8751A'; e.currentTarget.style.color = '#E8751A'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#e8e0d4'; e.currentTarget.style.color = '#6e5e50'; }}
                  >
                    {q}
                  </button>
                ))}
              </div>
              {/* 输入框 */}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={userInput}
                  onChange={e => setUserInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSend()}
                  placeholder="说说你的真实处境，或回应教练的提问..."
                  className="flex-1 py-2.5 px-4"
                  style={{
                    border: '1.5px solid #e8e0d4', borderRadius: '10px', fontSize: '0.82rem',
                    color: '#2c2420', background: '#fff', outline: 'none',
                  }}
                />
                <button
                  className="px-5 py-2.5 transition-all duration-200"
                  style={{
                    background: userInput.trim() ? '#1A2A4A' : '#e8e0d4',
                    color: userInput.trim() ? '#fff' : '#999',
                    borderRadius: '10px', fontSize: '0.8rem', fontWeight: 600,
                    cursor: userInput.trim() ? 'pointer' : 'not-allowed', border: 'none',
                  }}
                  onClick={handleSend}
                  disabled={!userInput.trim()}
                >
                  发送
                </button>
              </div>
            </div>

            {/* 返回按钮 */}
            <div className="text-center mt-4">
              <button
                className="py-2 transition-all duration-200"
                style={{
                  background: 'transparent', color: '#D94A4A',
                  border: 'none', fontSize: '0.85rem', fontWeight: 500,
                  cursor: 'pointer', letterSpacing: '0.02em',
                }}
                onMouseEnter={e => { e.currentTarget.style.color = '#ff6b6b'; }}
                onMouseLeave={e => { e.currentTarget.style.color = '#D94A4A'; }}
                onClick={() => { setView('scenes'); setUsage(getUsage()); }}
              >
                <span style={{ color: '#ff8080', marginRight: '4px' }}>←</span>返回场景选择
              </button>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════
            VIEW: 次数用尽
        ════════════════════════════════════════════ */}
        {view === 'limit' && (
          <div className="reveal-el mx-auto text-center" style={{ maxWidth: 420, padding: '3rem 1.5rem' }}>
            <div style={{
              width: 64, height: 64, borderRadius: '50%', background: '#fff8f3',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 1.5rem', fontSize: '2rem',
              boxShadow: '0 4px 16px rgba(232,117,26,0.15)',
            }}>💬</div>
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1A2A4A', marginBottom: '0.5rem' }}>
              本次体验已结束
            </h3>
            <p style={{ fontSize: '0.82rem', color: '#6e5e50', lineHeight: 1.7, marginBottom: '1.5rem' }}>
              针对您企业的具体情况，完整方案，需预约职场心理顾问。
            </p>
            <p style={{ fontSize: '0.72rem', color: '#a08e78', marginBottom: '1.5rem' }}>
              每人每天限{MAX_SESSIONS}次体验，每次{MAX_TURNS}轮对话
            </p>
            <a
              href={WEWORK_KF_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-10 py-3.5 transition-all duration-200 hover:scale-[1.02]"
              style={{
                background: 'linear-gradient(135deg, #E8751A, #d46816)', color: '#fff',
                borderRadius: '9999px', fontSize: '0.88rem', fontWeight: 600,
                textDecoration: 'none',
                boxShadow: '0 4px 16px rgba(232,117,26,0.3)',
              }}
            >
              预约职场心理顾问 →
            </a>
            <div className="mt-4">
              <button
                className="py-2 transition-all duration-200"
                style={{
                  background: 'transparent', color: '#D94A4A',
                  border: 'none', fontSize: '0.85rem', fontWeight: 500,
                  cursor: 'pointer', letterSpacing: '0.02em',
                }}
                onMouseEnter={e => { e.currentTarget.style.color = '#ff6b6b'; }}
                onMouseLeave={e => { e.currentTarget.style.color = '#D94A4A'; }}
                onClick={() => { setView('scenes'); setChatMsgs([]); setUsage(getUsage()); }}
              >
                <span style={{ color: '#ff8080', marginRight: '4px' }}>←</span>返回场景选择
              </button>
            </div>
          </div>
        )}

        {/* 底部说明 */}
        <p className="text-center mt-8 reveal-el" style={{ fontSize: '0.65rem', color: '#ccc' }}>
          每人每天限{MAX_SESSIONS}次体验，每次{MAX_TURNS}轮对话 · 对话内容仅用于体验展示
        </p>
      </div>

      <style>{`
        @keyframes typingBounce {
          0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
          40% { transform: translateY(-4px); opacity: 1; }
        }
      `}</style>
    </section>
  );
}
